import Checkbox from 'atoms/Checkbox';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { useStore } from 'stores/store';
import { IAggBucket, IFilter } from 'utils/globalTypes';
import { roundToOne, toPercentageFormat } from 'utils/math';
import { DATE_RANGE_FILTER_KEY } from 'utils/query';

import s from './FilterItem.module.scss';

const FilterItemDateRange = dynamic(() => import('./FilterItemDateRange'));

interface IFilterItem extends IAggBucket {
  isSelected?: boolean;
  filterField: string;
  filterKey: string;
  setFilters: (filters?: IFilter[]) => void;
  className?: string;
}

const getFilters = (filters?: IFilter[]) =>
  filters?.map((filter) => {
    if (filter.field === 'created_at') {
      return {
        field: filter.field,
        value: filter.value?.filter((v) => v !== DATE_RANGE_FILTER_KEY)
      };
    }
    return filter;
  });

const FilterItem = ({
  from,
  to,
  name,
  count,
  isSelected,
  filterField,
  filterKey,
  setFilters,
  className
}: IFilterItem) => {
  const store = useStore();
  const [dateRange, setDateRange] = useState<string>();

  const onChangeFilter = (value: string) => {
    let filters = !isSelected
      ? store.MarketplaceStore.addFilter(filterField, value)
      : store.MarketplaceStore.removeFilter(filterField, value);
    if (filterKey === DATE_RANGE_FILTER_KEY) {
      if (dateRange) {
        filters = store.MarketplaceStore.removeFilter(filterField, dateRange);
        setDateRange(undefined);
        setFilters(getFilters(filters));
      }
      return;
    }
    setFilters(getFilters(filters));
  };

  const buildRange = (from?: string | number, to?: string | number) => {
    if (!from && !to) return null;
    return (
      <>
        {from && !to && (
          <span>{`> ${toPercentageFormat({ value: from, callback: roundToOne })}`}</span>
        )}
        {from && to && (
          <span>{`${toPercentageFormat({ value: from, callback: roundToOne })} - 
              ${toPercentageFormat({ value: to, callback: roundToOne })}`}</span>
        )}
        {to && !from && (
          <span>{`< ${toPercentageFormat({ value: to, callback: roundToOne })}`}</span>
        )}
      </>
    );
  };

  const handeDateRangeChange = (timeRange: string) => {
    if (dateRange) {
      store.MarketplaceStore.removeFilter(filterField, dateRange);
    }
    setDateRange(timeRange);
    const filters = store.MarketplaceStore.addFilter(filterField, timeRange);
    setFilters(getFilters(filters));
  };

  const range = buildRange(from, to);

  return (
    <>
      <div className={cn(s.FilterItem__item, className)} key={filterKey}>
        <Checkbox
          id={`${filterField}-id-${filterKey}`}
          checked={isSelected}
          onChange={() => onChangeFilter(filterKey)}
          value={filterKey}
          title={filterKey}
        />
        <label className={s.FilterItem__item_content} htmlFor={`${filterField}-id-${filterKey}`}>
          {name || range || filterKey}
          {!!count && <span className={s.FilterItem__labelCount}>({count})</span>}
        </label>
      </div>
      {filterKey === DATE_RANGE_FILTER_KEY && isSelected && (
        <div className={s.FilterItem__fullWidth}>
          <FilterItemDateRange onChange={handeDateRangeChange} />
        </div>
      )}
    </>
  );
};

export default observer(FilterItem);
